import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Play, X } from "lucide-react";
import { useImages } from "../../lib/ImagesContext";
import { useContent } from "../../lib/ContentContext";
import { parseVideo } from "../../lib/videoEmbed";

const SOURCES = [
  { slot: "owned-hero", videoKey: "owned.video_main" },
  { slot: "owned-feed-1", videoKey: "owned.video_feed_1" },
  { slot: "owned-feed-2", videoKey: "owned.video_feed_2" },
  { slot: "owned-feed-3", videoKey: "owned.video_feed_3" },
  { slot: "owned-feed-4", videoKey: "owned.video_feed_4" },
];

export default function OwnedMedia() {
  const ref = useRef(null);
  const playerRef = useRef(null);
  const { getImg } = useImages();
  const { getText } = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 1.06]);
  const yImg = useTransform(scrollYProgress, [0, 1], [40, -40]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const active = SOURCES[activeIndex];
  const activeVideo = parseVideo(getText(active.videoKey));

  const goFullscreen = () => {
    const el = playerRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  // When the user leaves fullscreen (Esc / native control / our button),
  // return the player to the poster view.
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setPlaying(false);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  const closeVideo = () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen?.() || Promise.resolve()).catch?.(() => {});
      document.webkitExitFullscreen?.();
    }
    setPlaying(false);
  };

  const selectSource = (i) => {
    const vid = parseVideo(getText(SOURCES[i].videoKey));
    setActiveIndex(i);
    setPlaying(Boolean(vid));
    if (vid) goFullscreen();
  };

  const triggerPlay = () => {
    if (activeVideo) {
      setPlaying(true);
      goFullscreen();
    }
  };

  return (
    <section ref={ref} className="relative py-24 md:py-40 overflow-hidden" data-testid="owned-media-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-6">
            <div className="tag-glass"><span className="dot" /> XMLOOPS TV · Owned network</div>
            <h2 className="font-display text-white text-4xl md:text-6xl leading-[1.02] tracking-tight mt-6">
              A television <br />channel that <br />works <span className="italic-accent text-grad-crimson">for you</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <p className="text-white/72 text-lg leading-relaxed">
              Twenty-four hour programming across news, lifestyle, motorsport and business
              — with premium ad slots that are never resold. When you partner with us, you
              inherit the pipe, not just the pixel.
            </p>
            <div className="mt-8 flex items-center gap-6">
              <div>
                <div className="font-display text-4xl text-white">24 / 7</div>
                <div className="overline mt-2">Broadcast hours</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <div className="font-display text-4xl text-white">4K</div>
                <div className="overline mt-2">Studio grade</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <div className="font-display text-4xl text-white">India</div>
                <div className="overline mt-2">Nation-wide</div>
              </div>
            </div>
          </div>
        </div>

        {/* Clipped spotlight showcase */}
        <div className="relative mt-20 md:mt-28">
          <motion.div
            ref={playerRef}
            style={{ scale }}
            className="relative aspect-[16/9] rounded-[2px] overflow-hidden border border-white/10 bg-black"
            data-testid="owned-media-player"
          >
            {playing && activeVideo ? (
              <>
                {activeVideo.type === "embed" ? (
                  <iframe
                    key={activeVideo.src}
                    src={activeVideo.src}
                    title="XMLOOPS TV"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    data-testid="owned-media-iframe"
                  />
                ) : (
                  <video
                    key={activeVideo.src}
                    src={activeVideo.src}
                    poster={getImg(active.slot)}
                    controls
                    autoPlay
                    playsInline
                    className="absolute inset-0 w-full h-full object-contain bg-black"
                    data-testid="owned-media-video"
                  />
                )}
                <button
                  onClick={closeVideo}
                  className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/20 hover:bg-black/80 transition-colors flex items-center justify-center"
                  aria-label="Close video"
                  data-testid="owned-media-close"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </>
            ) : (
              <>
                <motion.img
                  style={{ y: yImg }}
                  src={getImg(active.slot)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />

                {/* Spotlight */}
                <div className="absolute inset-0 pointer-events-none"
                     style={{ background: "radial-gradient(circle at 65% 55%, rgba(0,240,255,0.18), transparent 40%)" }} />

                {/* Live indicator (visual only) */}
                <div className="absolute top-4 left-4">
                  <span className="block w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_10px_#ef4444]" />
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={triggerPlay}
                  disabled={!activeVideo}
                  aria-disabled={!activeVideo}
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-colors w-20 h-20 flex items-center justify-center ${activeVideo ? "hover:bg-white/20 cursor-pointer" : "opacity-50 cursor-default"}`}
                  aria-label="Play reel"
                  data-testid="owned-media-play"
                >
                  <Play className="w-7 h-7 text-white fill-white" />
                </motion.button>
              </>
            )}
          </motion.div>

          {/* Clipped side frames — each is a selectable video episode */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {SOURCES.slice(1).map((s, i) => {
              const idx = i + 1;
              const feedVideo = parseVideo(getText(s.videoKey));
              const isActive = activeIndex === idx;
              return (
                <motion.button
                  key={s.slot}
                  onClick={() => selectSource(idx)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.6 }}
                  className={`group relative aspect-[4/5] overflow-hidden border text-left transition-colors ${isActive ? "border-[#ff5865]" : "border-white/10 hover:border-white/30"}`}
                  data-testid={`owned-feed-${idx}`}
                >
                  <img src={getImg(s.slot)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />

                  {/* Play affordance when this feed has a video */}
                  {feedVideo && (
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center opacity-90 group-hover:scale-110 transition-transform">
                      <Play className="w-4 h-4 text-white fill-white" />
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
